/** Leaflet map pane: basemaps, shape rendering, drawing, selection sync (§4.4).
 *  Canvas renderer for the 200-shape/60fps budget. All quantities from geo.ts (Karney). */
import { useEffect, useRef, useState } from 'preact/hooks';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { BASEMAPS, DEFAULT_BASEMAP, FALLBACK_BASEMAP, GRANITE_ITEMS, MAX_ZOOM, basemapFor, inCountyCoverage } from '../providers/basemap';
import type { LatLng } from '../core/geo';
import type { ParcelResult, Shape } from '../core/types';

export interface ShapeView extends Shape {
  itemId?: string;
  verified: boolean;
  label: string;
}

export type ArmedTool =
  | { kind: 'item'; itemId: string; priceKind: 'area' | 'line' | 'count'; name: string }
  | { kind: 'boundary' }
  | null;

interface Props {
  center: LatLng;
  zoom: number;
  basemap: string;
  onBasemap: (b: string) => void;
  shapes: ShapeView[];
  parcel?: ParcelResult | null;
  selectedShapeId?: string | null;
  onSelectShape: (shapeId: string | null) => void;
  armed: ArmedTool;
  /** "point at it on the map": next click loads the parcel under it */
  picking: boolean;
  onPickPoint: (lat: number, lng: number) => void;
  /** [south, west, north, east] of what is on screen */
  onView?: (box: [number, number, number, number]) => void;
  /** parent-owned draft: MapPane only reports clicks and renders what it is given */
  draftPts: LatLng[];
  onMapClick: (pt: LatLng) => void;
  onEscape: () => void;
  onPinSite: (lat: number, lng: number, zoom: number) => void;
}

const PALO = '#476829', GRANITE = '#C8A76B', MESQUITE = '#26211B', PINK = '#C41E67';

export function MapPane(p: Props) {
  const divRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<L.Map | null>(null);
  const baseRef = useRef<L.TileLayer | null>(null);
  const underRef = useRef<L.TileLayer | null>(null);
  const shapesRef = useRef<L.LayerGroup | null>(null);
  const draftRef = useRef<L.LayerGroup | null>(null);
  const [imageryNote, setImageryNote] = useState<string | null>(null);
  const propsRef = useRef(p);
  propsRef.current = p;

  // init once
  useEffect(() => {
    const map = L.map(divRef.current!, {
      preferCanvas: true, zoomControl: true, attributionControl: true,
      center: p.center, zoom: p.zoom, maxZoom: MAX_ZOOM,
    });
    mapRef.current = map;
    shapesRef.current = L.layerGroup().addTo(map);
    draftRef.current = L.layerGroup().addTo(map);

    map.on('click', (e: L.LeafletMouseEvent) => {
      const cur = propsRef.current;
      if (cur.picking) { cur.onPickPoint(e.latlng.lat, e.latlng.lng); return; }
      if (!cur.armed) { cur.onSelectShape(null); return; }
      cur.onMapClick([e.latlng.lat, e.latlng.lng]);
    });
    map.on('dblclick', (e: L.LeafletMouseEvent) => {
      if (propsRef.current.armed) L.DomEvent.stop(e as unknown as Event);
    });
    map.doubleClickZoom.disable();

    const report = () => {
      const b = map.getBounds();
      propsRef.current.onView?.([b.getSouth(), b.getWest(), b.getNorth(), b.getEast()]);
      const dbg = (window as unknown as { __fgbsMap?: Record<string, unknown> });
      dbg.__fgbsMap = { ...(dbg.__fgbsMap ?? {}), zoom: map.getZoom(), maxZoom: map.getMaxZoom() };
    };
    map.on('moveend zoomend', report);
    report();

    const onKey = (ev: KeyboardEvent) => {
      if (ev.key === 'Escape' && propsRef.current.armed) propsRef.current.onEscape();
    };
    window.addEventListener('keydown', onKey);
    return () => { window.removeEventListener('keydown', onKey); map.remove(); mapRef.current = null; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** Two stacked layers, always. The chosen imagery sits on top; a nationwide layer
   *  sits under it. A tile the top layer does not have shows the one underneath
   *  instead of an empty gray square — the field failure on 2026-07-25 was a whole
   *  screen of empty gray squares with nothing on screen saying why.
   *
   *  No crossOrigin: the tiles are only ever displayed, never read back off a
   *  canvas, and requesting CORS makes any server that omits the header render
   *  nothing at all. */
  useEffect(() => {
    const map = mapRef.current; if (!map) return;
    const def = basemapFor(p.basemap);
    const fallback = basemapFor(FALLBACK_BASEMAP);
    setImageryNote(null);

    if (underRef.current) { underRef.current.remove(); underRef.current = null; }
    if (def.key !== fallback.key) {
      underRef.current = L.tileLayer(fallback.url, {
        attribution: fallback.attribution,
        maxNativeZoom: fallback.maxNativeZoom,
        maxZoom: MAX_ZOOM,
        zIndex: 1,
      }).addTo(map);
    }

    // Never let the map zoom deeper than the imagery can support. One level past
    // native is a 2x blow-up, which still reads; the field screenshot on
    // 2026-07-25 was a 32x blow-up of USGS, which reads as mush and looks like the
    // app is broken. If the current zoom is now too deep, pull back to the cap.
    const zoomCap = def.maxNativeZoom + 1;
    map.setMaxZoom(zoomCap);
    if (map.getZoom() > zoomCap) map.setZoom(zoomCap);

    if (baseRef.current) baseRef.current.remove();
    const top = L.tileLayer(def.url, {
      attribution: def.attribution,
      maxNativeZoom: def.maxNativeZoom,
      maxZoom: MAX_ZOOM,
      zIndex: 2,
      bounds: def.bounds ? L.latLngBounds(def.bounds[0], def.bounds[1]) : undefined,
    });
    // if the top layer is failing outright, say so rather than letting the user
    // wonder why last year's photo looks different
    let bad = 0, good = 0;
    top.on('tileload', () => { good++; });
    top.on('tileerror', () => {
      bad++;
      if (bad >= 4 && good === 0 && underRef.current) {
        setImageryNote(`${def.label} isn’t answering — showing ${fallback.label} instead.`);
      }
    });
    top.addTo(map);
    baseRef.current = top;
  }, [p.basemap]);

  // recenter when the job's pin changes
  useEffect(() => {
    mapRef.current?.setView(p.center, p.zoom);
  }, [p.center[0], p.center[1], p.zoom]);

  // shapes + parcel — rebuilding the layer is O(shapes); skip when nothing visual changed
  const sigRef = useRef('');
  useEffect(() => {
    const g = shapesRef.current; if (!g) return;
    const sig = p.shapes.map((s) => `${s.id}:${s.rev}:${s.verified ? 1 : 0}`).join(',')
      + `|${p.selectedShapeId ?? ''}|${p.parcel?.fetchedAt ?? ''}`;
    if (sig === sigRef.current) return;
    sigRef.current = sig;
    g.clearLayers();
    if (p.parcel?.rings?.length) {
      for (const ring of p.parcel.rings) {
        L.polygon(ring as L.LatLngTuple[], {
          color: '#ffffff', weight: 2.5, dashArray: '8 6', fill: false, interactive: false,
        }).addTo(g);
      }
    }
    for (const s of p.shapes) {
      const sel = s.id === p.selectedShapeId;
      const granite = s.itemId ? GRANITE_ITEMS.has(s.itemId) : false;
      const color = s.kind === 'line' ? MESQUITE : granite ? GRANITE : PALO;
      let layer: L.Layer;
      if (s.kind === 'polygon') {
        layer = L.polygon(s.coords as L.LatLngTuple[], {
          color: sel ? '#ffffff' : color, weight: sel ? 4 : 2.5,
          fillColor: color, fillOpacity: 0.32,
        });
      } else if (s.kind === 'line') {
        layer = L.polyline(s.coords as L.LatLngTuple[], {
          color: sel ? '#ffffff' : color, weight: sel ? 5 : 3.5,
        });
      } else {
        const gg = L.layerGroup();
        for (const c of s.coords) {
          L.circleMarker(c as L.LatLngTuple, {
            radius: sel ? 8 : 6, color: '#ffffff', weight: 1.5, fillColor: color, fillOpacity: 0.95,
          }).addTo(gg);
        }
        layer = gg;
      }
      layer.on('click', () => propsRef.current.onSelectShape(s.id));
      if (s.label) (layer as L.Polygon).bindTooltip?.(s.label, { sticky: true });
      layer.addTo(g);
      // verified shapes wear the pink tie at their first vertex — pink means field-verified, nowhere else
      if (s.verified && s.coords[0]) {
        L.marker(s.coords[0] as L.LatLngTuple, {
          interactive: false,
          icon: L.divIcon({ className: '', html: '<div style="width:11px;height:11px;transform:rotate(45deg);background:#C41E67;border:1.5px solid #fff;"></div>', iconSize: [11, 11], iconAnchor: [6, 6] }),
        }).addTo(g);
      }
    }
    (window as unknown as { __fgbsMap?: object }).__fgbsMap = {
      shapeCount: p.shapes.length, hasParcel: !!p.parcel?.rings?.length,
    };
  }, [p.shapes, p.parcel, p.selectedShapeId]);

  // cursor only — the draft itself is parent-owned, so no state races here
  useEffect(() => {
    mapRef.current?.getContainer() && (mapRef.current.getContainer().style.cursor = (p.armed || p.picking) ? 'crosshair' : '');
  }, [p.armed, p.picking]);

  // render the draft the parent gave us
  useEffect(() => { renderDraft(); }, [p.draftPts, p.armed]);

  function renderDraft() {
    const g = draftRef.current; if (!g) return;
    g.clearLayers();
    const pts = propsRef.current.draftPts;
    if (!pts.length) return;
    const armed = propsRef.current.armed;
    const isArea = armed?.kind === 'boundary' || (armed?.kind === 'item' && armed.priceKind === 'area');
    const isLine = armed?.kind === 'item' && armed.priceKind === 'line';
    const color = armed?.kind === 'boundary' ? '#ffffff' : isLine ? MESQUITE : PALO;
    if (pts.length >= 2 && (isArea || isLine)) {
      (isArea && pts.length >= 3
        ? L.polygon(pts as L.LatLngTuple[], { color, weight: 2.5, dashArray: '4 4', fillOpacity: 0.15 })
        : L.polyline(pts as L.LatLngTuple[], { color, weight: 2.5, dashArray: '4 4' })
      ).addTo(g);
    }
    for (const c of pts) {
      L.circleMarker(c as L.LatLngTuple, { radius: 4.5, color: '#fff', weight: 1.5, fillColor: color, fillOpacity: 1 }).addTo(g);
    }
  }

  return (
    <div class="on-map" style="position:relative;height:100%;min-height:420px;">
      <div ref={divRef} style="position:absolute;inset:0;border-radius:8px;" data-testid="map" />
      <div style="position:absolute;bottom:10px;left:10px;z-index:800;max-width:calc(100% - 20px);">
        <select class="mapsel" aria-label="Which photo to show"
          value={p.basemap} onChange={(e) => p.onBasemap((e.target as HTMLSelectElement).value)}>
          <optgroup label="Maricopa County photos — sharpest, newest first">
            {BASEMAPS.filter((b) => b.group === 'County aerial').map((b) => (
              <option key={b.key} value={b.key}>{b.label}</option>
            ))}
          </optgroup>
          <optgroup label="Anywhere else">
            {BASEMAPS.filter((b) => b.group === 'Nationwide').map((b) => (
              <option key={b.key} value={b.key}>{b.label}</option>
            ))}
          </optgroup>
        </select>
        {imageryNote && <div class="maptip" role="status">{imageryNote}</div>}
        {basemapFor(p.basemap).group !== 'County aerial' && inCountyCoverage(p.center[0], p.center[1]) && (
          <div class="maptip" role="status">
            <div>This is the low-detail nationwide photo. The county’s own flight of this
              address is far sharper.</div>
            <button type="button" class="btn btn-small btn-primary" style="margin-top:6px;"
              onClick={() => p.onBasemap(DEFAULT_BASEMAP)}>
              Use the county photo
            </button>
          </div>
        )}
      </div>
      <button type="button" class="btn btn-small btn-ghost"
        style="position:absolute;top:10px;right:10px;z-index:800;"
        title="Save this map position as the job's site location"
        onClick={() => {
          const m = mapRef.current; if (!m) return;
          const c = m.getCenter();
          p.onPinSite(c.lat, c.lng, m.getZoom());
        }}>
        Pin site here
      </button>
    </div>
  );
}
